/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../../shared/modals/Modal';
import { Ionicons } from '@expo/vector-icons';
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
  useWindowDimensions,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { spacing } from '../../shared/theme/spacing'
import { ticketColors } from '../../shared/theme/ticketColors'
import { typography } from '../../shared/theme/typography';
import { categoryAppliesToShortLabel } from '../domain/categoryOptions';
import type { CategoryOption } from '../domain/categoryOptions';
import { modalExperience } from '../../shared/modals/modalExperience.styles';
import { posSharedUi } from '../../shared/ui/posSharedUi';
import { TicketModalHeader } from '../../shared/ticket/TicketModalHeader';
import { matchesSearchText } from '../../shared/format/searchText';

const CATEGORY_MODAL_PAGE_SIZE = 8;

type CatalogCategorySelectorModalProps = {
  visible: boolean;
  title?: string;
  subtitle?: string;
  options: CategoryOption[];
  value: string;
  loading?: boolean;
  emptyLabel?: string;
  searchPlaceholder?: string;
  showScopePills?: boolean;
  onClose: () => void;
  onSelect: (categoryId: string) => void;
  /** Si no hay opciones, botón para abrir el alta de categoría (cierra este modal). */
  onRequestCreateCategory?: () => void;
};

export const CatalogCategorySelectorModal = ({
  visible,
  title = 'Elegir categoría',
  subtitle = 'Buscá una categoría y seleccionala para continuar.',
  options,
  value,
  loading = false,
  emptyLabel = 'No hay categorías para mostrar.',
  searchPlaceholder = 'Buscar categoría',
  showScopePills = true,
  onClose,
  onSelect,
  onRequestCreateCategory,
}: CatalogCategorySelectorModalProps) => {
  const insets = useSafeAreaInsets();
  const { width, height: screenHeight } = useWindowDimensions();
  const isDesktop = width >= 768;
  const [query, setQuery] = useState('');
  const [pageIndex, setPageIndex] = useState(0);

  const filteredOptions = useMemo(() => {
    if (!query.trim()) return options;
    return options.filter(
      (option) => matchesSearchText(option.label, query) || matchesSearchText(option.description, query),
    );
  }, [options, query]);

  const parentLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const option of options) map.set(option.id, option.label);
    return map;
  }, [options]);

  // Sin búsqueda: cada subcategoría va indentada debajo de su padre para que
  // se entienda la jerarquía al elegir (padre e hija son buckets distintos).
  const displayOptions = useMemo(() => {
    if (query.trim()) return filteredOptions;
    const optionIds = new Set(filteredOptions.map((option) => option.id));
    const childrenByParent = new Map<string, CategoryOption[]>();
    const roots: CategoryOption[] = [];
    for (const option of filteredOptions) {
      if (option.parentId && optionIds.has(option.parentId)) {
        const list = childrenByParent.get(option.parentId) ?? [];
        list.push(option);
        childrenByParent.set(option.parentId, list);
      } else {
        roots.push(option);
      }
    }
    const ordered: CategoryOption[] = [];
    for (const root of roots) {
      ordered.push(root);
      const children = childrenByParent.get(root.id);
      if (children) ordered.push(...children);
    }
    return ordered;
  }, [filteredOptions, query]);

  const totalFiltered = displayOptions.length;
  const totalPages = Math.max(1, Math.ceil(totalFiltered / CATEGORY_MODAL_PAGE_SIZE));

  const paginatedOptions = useMemo(() => {
    const start = pageIndex * CATEGORY_MODAL_PAGE_SIZE;
    return displayOptions.slice(start, start + CATEGORY_MODAL_PAGE_SIZE);
  }, [displayOptions, pageIndex]);

  const hasPagination = totalPages > 1;

  useEffect(() => {
    setPageIndex(0);
  }, [query, options, visible]);

  useEffect(() => {
    setPageIndex((p) => Math.min(p, Math.max(0, totalPages - 1)));
  }, [totalPages]);

  const handleClose = () => {
    setQuery('');
    onClose();
  };

  const handleSelect = (categoryId: string) => {
    onSelect(categoryId);
    setQuery('');
    onClose();
  };

  const sheetContent = (
    <>
      {!isDesktop ? <View style={styles.grabber} /> : null}

      <TicketModalHeader
        kicker="Catálogo"
        title={title}
        subtitle={subtitle}
        onClose={handleClose}
        compact={!isDesktop}
      />

      <TextInput
        style={styles.searchInput}
        value={query}
        onChangeText={setQuery}
        placeholder={searchPlaceholder}
        placeholderTextColor={ticketColors.ink3}
        autoCorrect={false}
        autoCapitalize="none"
        accessibilityLabel={searchPlaceholder}
      />

      <View style={styles.listMetaRow}>
        {loading ? (
          <>
            <ActivityIndicator size="small" color={ticketColors.ink2} />
            <Text style={styles.listMetaText}>Cargando…</Text>
          </>
        ) : (
          <Text style={styles.listMetaText}>
            {totalFiltered === 0
              ? 'Sin resultados'
              : `${totalFiltered} ${totalFiltered === 1 ? 'categoría' : 'categorías'}`}
          </Text>
        )}
        {query.trim() ? (
          <Pressable onPress={() => setQuery('')} hitSlop={8} accessibilityRole="button" accessibilityLabel="Limpiar búsqueda">
            <Text style={modalExperience.accentLink}>Limpiar</Text>
          </Pressable>
        ) : null}
      </View>

      <ScrollView style={styles.pickerScroll} contentContainerStyle={styles.pickerScrollContent} keyboardShouldPersistTaps="handled">
        {paginatedOptions.map((option) => {
          const isSelected = option.id === value;
          const customColor = option.color?.trim();
          const scopeLabel = option.appliesTo ? categoryAppliesToShortLabel(option.appliesTo) : null;
          const visibleScopeLabel = showScopePills ? scopeLabel : null;
          const description = option.description?.trim() ?? '';
          const parentLabel = option.parentId ? parentLabelById.get(option.parentId) : null;
          const a11ySuffix = visibleScopeLabel ? `, ${visibleScopeLabel}` : '';
          const a11yParent = parentLabel ? `, dentro de ${parentLabel}` : '';
          const a11ySelected = isSelected ? ', seleccionada' : '';

          return (
            <Pressable
              key={option.id}
              style={({ pressed }) => [
                styles.categoryRow,
                parentLabel && styles.categoryRowChild,
                isSelected && styles.categoryRowActive,
                pressed && !option.disabled && !isSelected && styles.pickerRowPressed,
                option.disabled && styles.pickerRowDisabled,
              ]}
              onPress={() => handleSelect(option.id)}
              disabled={option.disabled}
              accessibilityRole="button"
              accessibilityState={{ selected: isSelected }}
              accessibilityLabel={`${option.label}${a11ySuffix}${a11yParent}${a11ySelected}`}
            >
              {customColor ? (
                <View style={[styles.colorDot, { backgroundColor: customColor }]} accessibilityElementsHidden />
              ) : null}
              <View style={styles.optionTextBlock}>
                <Text
                  style={[styles.categoryRowText, isSelected && styles.categoryRowTextActive]}
                  numberOfLines={2}
                >
                  {option.label}
                </Text>
                {parentLabel ? (
                  <Text style={styles.optionMeta} numberOfLines={1}>
                    Dentro de {parentLabel}
                  </Text>
                ) : null}
                {visibleScopeLabel ? (
                  <Text style={styles.optionMeta} numberOfLines={1}>
                    {visibleScopeLabel}
                  </Text>
                ) : null}
                {description ? (
                  <Text style={styles.optionMeta} numberOfLines={1}>
                    {description}
                  </Text>
                ) : null}
              </View>
              {isSelected ? <Ionicons name="checkmark" size={18} color={ticketColors.gold} /> : null}
            </Pressable>
          );
        })}

        {!loading && filteredOptions.length === 0 ? (
          <View style={styles.emptyState}>
            <Text style={modalExperience.stateText}>{emptyLabel}</Text>
            <Text style={styles.emptyHint}>
              {onRequestCreateCategory
                ? 'Activá categorías del rubro en Categorías.'
                : 'Probá con otro texto de búsqueda.'}
            </Text>
            {onRequestCreateCategory ? (
              <Pressable
                style={({ pressed }) => [posSharedUi.primaryButton, styles.emptyCta, pressed && { opacity: 0.9 }]}
                onPress={() => {
                  setQuery('');
                  onClose();
                  onRequestCreateCategory();
                }}
                accessibilityRole="button"
                accessibilityLabel="Ir a categorías"
              >
                <Text style={posSharedUi.primaryButtonText}>Ir a categorías</Text>
              </Pressable>
            ) : null}
          </View>
        ) : null}
      </ScrollView>

      {!loading && hasPagination && totalFiltered > 0 ? (
        <View style={styles.pagerRow}>
          <Pressable
            onPress={() => setPageIndex((p) => Math.max(0, p - 1))}
            disabled={pageIndex === 0}
            style={({ pressed }) => [
              styles.pagerArrow,
              pageIndex === 0 && styles.pagerArrowDisabled,
              pressed && pageIndex > 0 && styles.pagerRowPressed,
            ]}
            accessibilityLabel="Página anterior de categorías"
            accessibilityRole="button"
          >
            <Ionicons name="chevron-back" size={18} color={pageIndex === 0 ? ticketColors.ink3 : ticketColors.ink} />
          </Pressable>
          <Text style={styles.pagerHint} numberOfLines={1}>
            {pageIndex + 1} / {totalPages}
          </Text>
          <Pressable
            onPress={() => setPageIndex((p) => Math.min(totalPages - 1, p + 1))}
            disabled={pageIndex >= totalPages - 1}
            style={({ pressed }) => [
              styles.pagerArrow,
              pageIndex >= totalPages - 1 && styles.pagerArrowDisabled,
              pressed && pageIndex < totalPages - 1 && styles.pagerRowPressed,
            ]}
            accessibilityLabel="Página siguiente de categorías"
            accessibilityRole="button"
          >
            <Ionicons name="chevron-forward" size={18} color={pageIndex >= totalPages - 1 ? ticketColors.ink3 : ticketColors.ink} />
          </Pressable>
        </View>
      ) : null}
    </>
  );

  if (isDesktop) {
    return (
      <Modal visible={visible} transparent animationType="fade" onRequestClose={handleClose}>
        <View style={[styles.sheetBackdrop, { justifyContent: 'center', alignItems: 'center' }]}>
          <Pressable
            style={StyleSheet.absoluteFill}
            onPress={handleClose}
            accessibilityRole="button"
            accessibilityLabel="Cerrar"
          />
          <View style={[styles.sheetCard, { maxWidth: 480, borderRadius: 16, paddingBottom: Math.max(insets.bottom, spacing.sm), maxHeight: screenHeight * 0.85 }]}>
            {sheetContent}
          </View>
        </View>
      </Modal>
    );
  }

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={handleClose}>
      <View style={styles.sheetBackdrop}>
        <Pressable
          style={StyleSheet.absoluteFill}
          onPress={handleClose}
          accessibilityRole="button"
          accessibilityLabel="Cerrar"
        />
        <View
          style={[styles.sheetCard, { paddingBottom: Math.max(insets.bottom, spacing.sm), maxHeight: screenHeight * 0.85 }]}
        >
          {sheetContent}
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  sheetBackdrop: {
    flex: 1,
    backgroundColor: ticketColors.dim,
    justifyContent: 'flex-end',
  },
  sheetCard: {
    backgroundColor: ticketColors.paper,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    paddingTop: 8,
    borderTopWidth: 1,
    borderColor: ticketColors.ruleSoft,
    overflow: 'hidden',
    zIndex: 1,
  },
  grabber: {
    width: 28,
    height: 3,
    borderRadius: 2,
    backgroundColor: ticketColors.rule,
    alignSelf: 'center',
    marginTop: 2,
    marginBottom: 12,
  },
  searchInput: {
    marginHorizontal: 20,
    marginTop: 12,
    marginBottom: 12,
    height: 44,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    paddingHorizontal: 14,
    fontSize: 15,
    color: ticketColors.ink,
  },
  listMetaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    gap: spacing.sm,
    minHeight: 22,
    marginBottom: 4,
  },
  listMetaText: {
    ...typography.caption,
    color: ticketColors.ink3,
    flex: 1,
  },
  categoryRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.sm,
    paddingVertical: 14,
    paddingHorizontal: 14,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper,
  },
  categoryRowActive: {
    borderColor: ticketColors.gold,
    backgroundColor: ticketColors.gold,
  },
  categoryRowChild: {
    marginLeft: 18,
    borderLeftWidth: 2,
    borderLeftColor: ticketColors.ruleSoft,
  },
  categoryRowText: {
    fontSize: 15,
    color: ticketColors.ink,
    flex: 1,
  },
  categoryRowTextActive: {
    color: ticketColors.goldInk,
    fontWeight: '700',
  },
  colorDot: {
    width: 10,
    height: 10,
    borderRadius: 999,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  optionTextBlock: {
    flex: 1,
    minWidth: 0,
    gap: 2,
  },
  optionMeta: {
    ...typography.caption,
    color: ticketColors.ink3,
    fontSize: 11,
  },
  pickerRowPressed: {
    backgroundColor: ticketColors.paper2,
    borderRadius: 8,
  },
  pickerRowDisabled: {
    opacity: 0.5,
  },
  emptyState: {
    paddingVertical: spacing.lg,
    paddingHorizontal: spacing.sm,
    alignItems: 'center',
    gap: spacing.sm,
  },
  emptyHint: {
    ...typography.caption,
    color: ticketColors.ink2,
    textAlign: 'center',
    lineHeight: 18,
    maxWidth: 300,
  },
  emptyCta: {
    marginTop: spacing.xs,
    alignSelf: 'stretch',
  },
  pickerScroll: {
    maxHeight: 320,
  },
  pickerScrollContent: {
    paddingHorizontal: 20,
    gap: 8,
  },
  pagerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: spacing.sm,
    marginHorizontal: 20,
    marginTop: 8,
  },
  pagerArrow: {
    width: 36,
    height: 36,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    backgroundColor: ticketColors.paper2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pagerArrowDisabled: {
    opacity: 0.4,
  },
  pagerRowPressed: {
    opacity: 0.7,
  },
  pagerHint: {
    fontSize: 13,
    color: ticketColors.ink3,
    fontWeight: '600',
    minWidth: 48,
    textAlign: 'center',
  },
});
